import { useState } from "react";
import { Link } from "react-router-dom";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { ArrowLeft, Banknote, CheckCircle2, CreditCard, Landmark, Loader2, Store } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useContent } from "@/lib/content";
import { useCart } from "@/lib/cart";
import { CartLines, money } from "@/components/site/Cart";
import { PageLoader } from "@/pages/Home";
import { AgreeNote } from "@/pages/Policy";

const Shell = ({ children }) => (
  <div className="wm shop-page" data-testid="checkout-page">
    <header className="look-top">
      <Link to="/shop" className="look-back" data-testid="checkout-back-link"><ArrowLeft size={18} /> Shop</Link>
      <Link to="/" className="logo logo-black"><img src="/brand/logo-black.png" alt="Wax Moi Day Spa" width="835" height="240" /></Link>
    </header>
    <main className="wrap checkout">{children}</main>
  </div>
);

const Details = ({ onCreated }) => {
  const cart = useCart();
  const [f, setF] = useState({ name: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/orders", { ...f, items: cart.items.map((i) => ({ product_id: i.id, qty: i.qty })) });
      onCreated(data);
    } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form className="form checkout-form" onSubmit={submit} data-testid="checkout-form">
      <h2>Your details</h2>
      <input required placeholder="Full name" autoComplete="name" value={f.name} onChange={set("name")} data-testid="checkout-name-input" />
      <input required type="email" inputMode="email" placeholder="Email for your receipt" autoComplete="email" value={f.email} onChange={set("email")} data-testid="checkout-email-input" />
      <input required type="tel" inputMode="tel" placeholder="Phone number" autoComplete="tel" value={f.phone} onChange={set("phone")} data-testid="checkout-phone-input" />
      <textarea placeholder="Notes (optional)" value={f.notes} onChange={set("notes")} data-testid="checkout-notes-input" />
      <p className="pay-hold"><Store size={14} /><span>Pickup at Wax Moi Day Spa, The Marathon Mall, Nassau. We'll email you when it's ready.</span></p>
      {error && <p className="form-error" data-testid="checkout-error">{error}</p>}
      <button className="btn gold" type="submit" disabled={busy} data-testid="checkout-continue-button">{busy ? <Loader2 size={16} className="spin" /> : `Continue to payment · ${money(cart.total)}`}</button>
      <AgreeNote action="ordering" />
    </form>
  );
};

const PickupOption = ({ c, busy, onReserve, only }) => (
  <div className="pay-opt" data-testid="order-pay-pickup">
    <p className="pay-opt-title"><Banknote size={16} /> {only ? "Pay in store at pickup" : "Pay at pickup"}</p>
    <p className="muted-sm">{only ? "We'll hold your items at the spa — pay by card or cash when you collect them." : "Reserve your items now and pay by card or cash at the spa."} Questions? Call <a href={`tel:${c.contact.phone_call}`}>{c.contact.phone_call_label}</a>.</p>
    <button type="button" className={`btn ${only ? "gold" : "light"}`} disabled={busy} onClick={onReserve} data-testid="order-reserve-button">{busy ? <Loader2 size={16} className="spin" /> : "Reserve & pay at pickup"}</button>
  </div>
);

const Pay = ({ state, onDone }) => {
  const c = useContent().data;
  const { order, payment } = state;
  const auth = { order_id: order.id, token: order.public_token };
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const reserve = async () => {
    setBusy(true);
    try { onDone((await api.post("/orders/reserve", auth)).data.order); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };
  if (!payment.online) return (
    <div className="form pay-step" data-testid="order-payment-step">
      <p className="eyebrow"><Store size={12} /> Order {order.number}</p>
      <h3 data-testid="order-total">Total {money(order.total)}</h3>
      {error && <p className="form-error" data-testid="order-payment-error">{error}</p>}
      <PickupOption c={c} busy={busy} onReserve={reserve} only />
    </div>
  );
  return (
    <div className="form pay-step" data-testid="order-payment-step">
      <p className="eyebrow"><CreditCard size={12} /> Order {order.number}</p>
      <h3 data-testid="order-total">Pay {money(order.total)}</h3>
      {error && <p className="form-error" data-testid="order-payment-error">{error}</p>}
      <div className="pay-opt" data-testid="order-pay-paypal">
        <p className="pay-opt-title"><CreditCard size={16} /> PayPal or debit / credit card</p>
        <PayPalScriptProvider options={{ clientId: payment.paypal.client_id, currency: "USD", intent: "capture", components: "buttons", enableFunding: "card" }}>
          <PayPalButtons style={{ layout: "vertical", shape: "rect", color: "gold", label: "pay" }}
            createOrder={async () => { setError(""); try { return (await api.post("/orders/paypal/order", auth)).data.paypal_order_id; } catch (err) { setError(formatApiError(err)); throw err; } }}
            onApprove={async (d) => {
              try { const { data } = await api.post("/orders/paypal/capture", { ...auth, paypal_order_id: d.orderID }); toast.success("Payment received — thank you!"); onDone(data.order); }
              catch (err) { setError(formatApiError(err)); }
            }}
            onError={() => setError("Payment could not be completed. Please try again or reserve and pay at pickup.")} />
        </PayPalScriptProvider>
      </div>
      <div className="pay-opt"><p className="pay-opt-title"><Landmark size={16} /> Bahamas bank card</p><p className="muted-sm">Coming soon — local card payments through our bank.</p></div>
      <PickupOption c={c} busy={busy} onReserve={reserve} />
    </div>
  );
};

const Done = ({ order }) => (
  <div className="form confirm-box" data-testid="order-confirmation">
    <CheckCircle2 size={36} color="var(--gold)" />
    <h3 data-testid="order-confirmation-title">{order.status === "paid" ? "Thank you — you're paid!" : "Your items are reserved"}</h3>
    <p>Order <strong data-testid="order-number">{order.number}</strong> · {money(order.total)}</p>
    <p className="muted-sm">A receipt is on its way to <strong>{order.email}</strong>. We'll let you know when your order is ready for pickup.</p>
    <Link to="/shop" className="btn light" data-testid="order-continue-shopping">Continue shopping</Link>
  </div>
);

export default function Checkout() {
  const c = useContent().data;
  const cart = useCart();
  const [state, setState] = useState(null);
  if (!c) return <PageLoader />;
  const done = (order) => { cart.clear(); setState({ done: order }); window.scrollTo(0, 0); };
  if (state?.done) return <Shell><Done order={state.done} /></Shell>;
  if (!cart.count && !state) return <Shell><p className="muted-sm" data-testid="checkout-empty">Your bag is empty. <Link to="/shop">Browse the shop</Link></p></Shell>;
  return (
    <Shell>
      <h1 className="checkout-title">Checkout</h1>
      <div className="checkout-grid">
        <section className="checkout-summary" data-testid="checkout-summary">
          <CartLines editable={!state} />
          <p className="cart-total"><span>Total</span><b data-testid="checkout-total">{money(state?.order.total ?? cart.total)}</b></p>
        </section>
        {state ? <Pay state={state} onDone={done} /> : <Details onCreated={(d) => { setState(d); window.scrollTo(0, 0); }} />}
      </div>
    </Shell>
  );
}
